'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';

import {
  Avatar,
  Button,
  IconPerson,
  AvatarButton,
  Card,
  IconX,
} from '@veneer/core';
import { getSession } from 'next-auth/react';
import { env } from 'next-runtime-env';
import { useTranslation } from 'react-i18next';

import { parseJWT } from '@storefront/app/api/auth/[...nextauth]/auth.utils';
import ModalSignOut from '@storefront/app/v2/(purchase)/checkout/shared-components/modal-sign-out/modal-sign-out';
import { checkKeysPressed } from '@storefront/utils/utils';

import styles from './user-session-avatar.module.scss';

const UserSessionAvatar = () => {
  const { t } = useTranslation();
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [userNameFull, setUserNameFull] = useState<string>('');
  const [userNameInitials, setUserNameInitials] = useState<string>('');
  const avatarRef = useRef<HTMLDivElement>(null);

  const getUserName = useCallback(
    async (useInitials: boolean = false): Promise<string> => {
      const session = await getSession();
      if (session) {
        const { family_name, given_name } = parseJWT(session['id_token']);
        if (useInitials) {
          return `${given_name.charAt(0)}${family_name.charAt(
            0
          )}`.toUpperCase();
        }
        return `${given_name} ${family_name}`;
      }
      return '';
    },
    []
  );

  useEffect(() => {
    getUserName(false).then((name) => setUserNameFull(name));
    getUserName(true).then((name) => setUserNameInitials(name));
  }, [getUserName]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      avatarRef.current &&
      !avatarRef.current.contains(event.target as Node)
    ) {
      setShowAvatarModal(false);
    }
  }, []);

  const handleScroll = useCallback(() => {
    setShowAvatarModal(false);
  }, []);

  useEffect(() => {
    if (showAvatarModal) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('scroll', handleScroll);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [showAvatarModal, handleClickOutside, handleScroll]);

  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const closeModal = useCallback(() => setShowAvatarModal(false), []);
  const renderAvatarButton = () => (
    <AvatarButton
      label={userNameInitials}
      className={styles['header-icon']}
      onClick={() => setShowAvatarModal(!showAvatarModal)}
      onKeyDown={(event) => {
        checkKeysPressed(event, setShowAvatarModal, !showAvatarModal);
      }}
      tabIndex={0}
      data-testid="IconPersonCircle"
    />
  );

  return (
    <div id="header-avatar" ref={avatarRef}>
      {isTablet && showAvatarModal ? (
        <button
          onClick={() => setShowAvatarModal(false)}
          onKeyDown={(event) => {
            checkKeysPressed(event, setShowAvatarModal, false);
          }}
          tabIndex={0}
          className={styles['close-button-header']}
        >
          <IconX />
        </button>
      ) : (
        renderAvatarButton()
      )}
      {showAvatarModal && (
        <Card
          border="dropShadow"
          id="avatar-card"
          className={styles['avatar-card']}
        >
          <button
            className={styles['close-button']}
            onClick={closeModal}
            aria-label={t('button.close')}
            type="button"
          >
            <IconX />
          </button>
          <div className={styles['avatar-content']}>
            <div className={styles['avatar-section']}>
              <Avatar
                label={userNameInitials}
                className={styles['avatar-large']}
                aria-label={t('button.personCircle')}
                data-testid="IconPersonCircleModal"
              />
              <p className={`${styles['welcome-text']} body-large`}>
                {t('avatar.welcome', { name: userNameFull })}
              </p>
            </div>
            <button
              type="button"
              className={styles['account-section']}
              onClick={() =>
                window.open(
                  env('NEXT_PUBLIC_MY_ACCOUNT'),
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  window.open(
                    env('NEXT_PUBLIC_MY_ACCOUNT'),
                    '_blank',
                    'noopener,noreferrer'
                  );
                }
              }}
              aria-label={t('avatar.title')}
            >
              <IconPerson className={styles['avatar-small']} />
              <span className={`${styles['account-label']} body-large`}>
                {t('avatar.title')}
              </span>
            </button>

            <div className={styles['button-section']}>
              <Button
                appearance="secondary"
                className={styles['sign-out-button']}
                onClick={() => setShowModal(true)}
              >
                {t('avatar.button')}
              </Button>
            </div>
          </div>
        </Card>
      )}
      <ModalSignOut setShowModal={setShowModal} showModal={showModal} />
    </div>
  );
};

export default UserSessionAvatar;
